import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../auth/authService';
import Unauthorized from './Unauthorized';
import { isPatientMode } from '../utils/patientMode';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component
 * Protects routes from unauthorized access
 * Redirects to login if user is not authenticated
 * For viewer routes with StudyInstanceUIDs, redirects to patient login
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAuthenticated = authService.isAuthenticated();

  useEffect(() => {
    // Refresh RIS tokens only (patient sessions have no refresh token)
    if (authService.isRisAuthenticated() && authService.shouldRefreshToken()) {
      authService.refreshToken().catch(() => {
        // If refresh fails, user will be redirected to login on next check
      });
    }
  }, [location.pathname]);

  if (!isAuthenticated) {
    // Check if this is a viewer route with StudyInstanceUIDs
    const studyInstanceUIDs = searchParams.get('StudyInstanceUIDs');
    const isViewerRoute = location.pathname.includes('/viewer');

    // If accessing viewer with StudyInstanceUIDs, redirect to patient login
    if (isViewerRoute && studyInstanceUIDs) {
      return (
        <Navigate
          to={`/patient/login?StudyInstanceUIDs=${studyInstanceUIDs}`}
          state={{ from: location }}
          replace
        />
      );
    }

    // Otherwise redirect to regular login page, but save the attempted location
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check if patient is trying to access restricted routes
  const patientMode = isPatientMode();
  const isRootRoute = location.pathname === '/' || location.pathname === '';
  const isViewerRoute = location.pathname.includes('/viewer');
  const studyInstanceUIDs = searchParams.get('StudyInstanceUIDs');

  if (isAuthenticated && patientMode) {
    // Patients cannot access the worklist/study list
    if (isRootRoute) {
      return <Unauthorized />;
    }

    // Patients accessing viewer route must have StudyInstanceUIDs
    if (isViewerRoute && !studyInstanceUIDs) {
      // Patient removed StudyInstanceUIDs parameter - show unauthorized
      return <Unauthorized />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
